<template>
  <div class="block-wrapper">
    <div class="row">
      <div class="col-6">
        <p>Section title</p>
        <el-input
          v-model="blockContent.content.title"
          placeholder="Curated artworks"
        />
      </div>
      <div class="col-6">
        <p>Section subtitle</p>
        <el-input
          v-model="blockContent.content.subtitle"
          placeholder="A curated spotlight on technical excellence"
        />
      </div>
    </div>
    <p class="mt-2">Add artworks</p>
    <el-select
      v-model="pendingArtwork"
      class="mt-2"
      style="width: 100%"
      filterable
      remote
      clearable
      reserve-keyword
      placeholder="Search by artwork title"
      :remote-method="remoteArtworkSearch"
      :loading="loading"
      value-key="id"
      @change="onArtworkPicked"
    >
      <el-option
        v-for="item in artworkOptions"
        :key="item.id"
        :label="artworkSelectLabel(item)"
        :value="item"
      />
    </el-select>
    <div v-if="blockContent.content.artworks.length > 0" class="mt-2">
      <draggable
        :list="blockContent.content.artworks"
        item-key="id"
        class="artworks-draggable-list"
        ghost-class="ghost-artwork-row"
        handle=".drag-handle"
      >
        <template #item="{ element: row, index: i }">
          <div class="relative-parent artwork-draggable-row">
            <span
              class="drag-handle"
              title="Drag to reorder"
              aria-label="Drag to reorder"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 320 512"
                width="14"
                height="14"
                fill="currentColor"
              >
                <path
                  d="M40 352l48-48c9.4-9.4 24.6-9.4 33.9 0l48 48c9.4 9.4 9.4 24.6 0 33.9l-48 48c-9.4 9.4-24.6 9.4-33.9 0l-48-48c-9.4-9.4-9.4-24.6 0-33.9zm192-224l-48 48c-9.4 9.4-24.6 9.4-33.9 0l-48-48c-9.4-9.4-9.4-24.6 0-33.9l48-48c9.4-9.4 24.6-9.4 33.9 0l48 48c9.4 9.4 9.4 24.6 0 33.9zM40 160l48 48c9.4 9.4 24.6 9.4 33.9 0l48-48c9.4-9.4 9.4-24.6 0-33.9l-48-48c-9.4-9.4-24.6-9.4-33.9 0l-48 48c-9.4 9.4-9.4 24.6 0 33.9zm192 192l-48-48c-9.4-9.4-24.6-9.4-33.9 0l-48 48c-9.4 9.4-9.4 24.6 0 33.9l48 48c9.4 9.4 24.6 9.4 33.9 0l48-48c9.4-9.4 9.4-24.6 0-33.9z"
                />
              </svg>
            </span>
            <p class="selected-row-label">
              {{ row.title }} — {{ row.artist_name }}
            </p>
            <div
              class="delete-row"
              @click="() => blockContent.content.artworks.splice(i, 1)"
            >
              <svg
                class="svg-inline--fa fa-trash-alt fa-w-14"
                aria-hidden="true"
                focusable="false"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
              >
                <path
                  fill="currentColor"
                  d="M32 464a48 48 0 0 0 48 48h288a48 48 0 0 0 48-48V128H32zm272-256a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zm-96 0a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zm-96 0a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zM432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16z"
                />
              </svg>
            </div>
          </div>
        </template>
      </draggable>
    </div>
  </div>
  <el-button
    @click="deleteBlock(blockData.id)"
    class="mt-2"
    size="small"
    type="danger"
    >Delete this block</el-button
  >
</template>

<script>
import draggable from "vuedraggable";

export default {
  name: "CuratedArtworksBlock",
  components: {
    draggable,
  },
  props: {
    blockData: Object,
  },
  data() {
    return {
      loading: false,
      pendingArtwork: null,
      artworkOptions: [],
      blockContent: {
        content: {
          title: "",
          subtitle: "",
          artworks: [],
        },
      },
    };
  },
  created() {
    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === "COLLECT_BLOCK_CONTENT") {
        this.$store.dispatch("updateBlockContent", {
          blockData: this.blockData,
          blockContent: this.blockContent,
        });
      }
    });
    const persisted =
      this.blockData.content || this.blockData.block_content || {};
    if (this.blockData.created_at || Object.keys(persisted).length > 0) {
      this.blockContent.content = {
        title: persisted.title || "",
        subtitle: persisted.subtitle || "",
        artworks: Array.isArray(persisted.artworks) ? persisted.artworks : [],
      };
    }
  },
  beforeUnmount() {
    this.unsubscribe();
  },
  methods: {
    artworkSelectLabel(item) {
      const artist = item.artist?.name || "";
      return artist ? `${item.title} — ${artist}` : item.title;
    },
    normalizeArtwork(art) {
      return {
        id: art.id,
        slug: art.slug,
        title: art.title || "",
        artist_name: art.artist?.name || "",
        year:
          art.year !== null && art.year !== undefined ? String(art.year) : "",
        image_slug: art.featured_image?.slug || "",
      };
    },
    onArtworkPicked(art) {
      if (!art || !art.id) {
        this.pendingArtwork = null;
        return;
      }
      if (this.blockContent.content.artworks.some((a) => a.id === art.id)) {
        this.pendingArtwork = null;
        return;
      }
      this.blockContent.content.artworks.push(this.normalizeArtwork(art));
      this.pendingArtwork = null;
      this.artworkOptions = [];
    },
    deleteBlock(id) {
      this.$store.dispatch("removeBlockFromList", id);
    },
    remoteArtworkSearch(query) {
      if (!query) {
        this.artworkOptions = [];
        return;
      }
      this.loading = true;
      this.artworkOptions = [];
      this.$api
        .get(
          `${process.env.VUE_APP_SHARED_API}api/artworks?search=${encodeURIComponent(
            query.toLowerCase()
          )}&per_page=20`
        )
        .then((res) => {
          if (res.data.data) {
            this.artworkOptions = res.data.data;
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.block-wrapper {
  border: #adb2ad solid 1px;
  padding: 16px 20px;
}
.mt-2 {
  margin-top: 12px;
}
.artworks-draggable-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.delete-row {
  position: absolute;
  top: 50%;
  right: 5px;
  transform: translateY(-50%);
  height: 20px;
  width: 20px;
  cursor: pointer;
  display: flex;
  align-content: center;
  justify-content: center;
  background-color: red;
  border-radius: 50%;
  align-items: center;
  z-index: 1;
}
.delete-row > svg {
  color: white;
  fill: white;
  width: 12px;
  height: 12px;
}
.relative-parent {
  position: relative;
}
.artwork-draggable-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 36px 8px 4px;
  border: 1px solid #e0e0e0;
}
.drag-handle {
  flex-shrink: 0;
  cursor: grab;
  color: #909399;
  display: flex;
  align-items: center;
  padding: 4px 6px;
  user-select: none;
}
.drag-handle:active {
  cursor: grabbing;
}
.selected-row-label {
  flex: 1;
  margin: 0;
  font-size: 13px;
  min-width: 0;
  text-align: left;
}
.ghost-artwork-row {
  opacity: 0.55;
}
</style>
